import { useCallback, useEffect, useState } from 'react';
import { Copy, FileCode2, Pencil, Save } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { LoadingState } from '../components/ui/loading-state';
import { Modal } from '../components/ui/modal';
import { PageHeader } from '../components/ui/page-header';
import { StatusBadge } from '../components/ui/status-badge';
import { DocumentTemplateDto } from '../models';
import { copySystemDocumentTemplate, getApiError, getDocumentTemplate, getDocumentTemplates, updateDocumentTemplate } from '../services/api';

export const TemplatesPage = () => {
  const [items, setItems] = useState<DocumentTemplateDto[]>();
  const [editing, setEditing] = useState<DocumentTemplateDto>();
  const [headerHtml, setHeaderHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try { setItems((await getDocumentTemplates()).data.data); }
    catch (e) { setError(getApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (tenantId: number) => {
    setSubmitting(true); setError(''); setSuccess('');
    try {
      await copySystemDocumentTemplate(tenantId);
      setSuccess('Системный шаблон скопирован в организацию');
      await load();
    } catch (e) { setError(getApiError(e)); }
    finally { setSubmitting(false); }
  };

  const openEditor = async (tenantId: number) => {
    setError('');
    try {
      const item = (await getDocumentTemplate(tenantId)).data.data;
      setEditing(item);
      setHeaderHtml(item.header_html || '');
      setFooterHtml(item.footer_html || '');
    } catch (e) { setError(getApiError(e)); }
  };

  const save = async () => {
    if (!editing) return;
    setSubmitting(true); setError(''); setSuccess('');
    try {
      await updateDocumentTemplate(editing.tenant_id, headerHtml, footerHtml);
      setSuccess(`Шаблон «${editing.tenant_name}» сохранён`);
      setEditing(undefined);
      await load();
    } catch (e) { setError(getApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <AppLayout title="Шаблоны документов">
      <PageHeader title="Шаблоны документов" description="HTML-шапка и подвал для направлений, дневников давления и документов по алкоголю." />
      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}
      {!items ? <LoadingState /> : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead><tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground"><th className="px-4 py-3">Организация</th><th className="px-4 py-3">Статус</th><th className="px-4 py-3">Размер</th><th className="px-4 py-3">Изменён</th><th className="px-4 py-3 text-right">Действия</th></tr></thead>
              <tbody>{items.map((item) => (
                <tr key={item.tenant_id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-4"><div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-primary"><FileCode2 className="h-4 w-4" /></div><div><p className="font-semibold">{item.tenant_name}</p><p className="text-xs text-muted-foreground">Tenant #{item.tenant_id}</p></div></div></td>
                  <td className="px-4 py-4"><StatusBadge label={item.is_active ? 'Активен' : 'Отсутствует'} variant={item.is_active ? 'success' : 'destructive'} /></td>
                  <td className="px-4 py-4 text-xs text-muted-foreground">Шапка: {item.header_length} · Подвал: {item.footer_length}</td>
                  <td className="px-4 py-4 text-xs text-muted-foreground">{item.updated_date || item.created_date ? new Date(item.updated_date || item.created_date!).toLocaleString('ru-RU') : '—'}</td>
                  <td className="px-4 py-4"><div className="flex justify-end gap-2">{item.is_active ? <Button size="sm" variant="outline" onClick={() => openEditor(item.tenant_id)}><Pencil className="h-3.5 w-3.5" /> Редактировать</Button> : <Button size="sm" disabled={submitting} onClick={() => copy(item.tenant_id)}><Copy className="h-3.5 w-3.5" /> Скопировать системный</Button>}</div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={Boolean(editing)} onClose={() => setEditing(undefined)} title={`Шаблон: ${editing?.tenant_name || ''}`} maxWidth="max-w-5xl"
        footer={<><Button variant="ghost" onClick={() => setEditing(undefined)}>Отмена</Button><Button disabled={submitting} onClick={save}><Save className="h-4 w-4" />{submitting ? 'Сохранение...' : 'Сохранить'}</Button></>}
      >
        <div className="grid gap-5">
          <label className="space-y-2 text-sm font-semibold">HTML шапки<textarea value={headerHtml} onChange={(e) => setHeaderHtml(e.target.value)} spellCheck={false} className="min-h-[220px] w-full rounded-xl border border-border bg-foreground p-4 font-mono text-xs font-normal leading-5 text-white outline-none focus:border-primary" /></label>
          <label className="space-y-2 text-sm font-semibold">HTML подвала<textarea value={footerHtml} onChange={(e) => setFooterHtml(e.target.value)} spellCheck={false} className="min-h-[220px] w-full rounded-xl border border-border bg-foreground p-4 font-mono text-xs font-normal leading-5 text-white outline-none focus:border-primary" /></label>
        </div>
      </Modal>
    </AppLayout>
  );
};
